import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Database, Menu, X, FolderOpen, Users, Send, BookOpen } from 'lucide-react';
import UnifiedTableView from '../components/UnifiedTableView';
import LoadingSpinner from '../components/LoadingSpinner';
import { useData } from '../contexts/DataContext';

// Helper function to check if dev mode is enabled via URL parameter
const checkDevMode = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('dev') === 'true';
  }
  return false;
};

// Main dashboard content component
const Dashboard: React.FC = () => {
  const { getAllProjects, getAllComponents, isLoading, error } = useData();
  const isDevMode = checkDevMode();
  
  const projects = getAllProjects();
  const allComponents = getAllComponents();

  // Debug logging in dev mode
  if (isDevMode) {
    console.log('Dashboard: Projects:', projects);
    console.log('Dashboard: All components:', allComponents);
    console.log('Dashboard: Total components from array:', allComponents.length);
    console.log('Dashboard: Components per project:', projects.map(p => ({
      name: p.name,
      componentsCount: p.componentsCount,
      componentsLength: p.components?.length || 0,
      components: p.components
    })));
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Error Loading Dashboard</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate total components - use components array if available, otherwise fallback to componentsCount
  const totalComponents = allComponents.length > 0 
    ? allComponents.length 
    : projects?.reduce((sum, p) => sum + (p.componentsCount || 0), 0) || 0;

  return (
    <div className="min-h-screen">
      {/* Dashboard Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold gradient-text mb-2">
          PatternLab Dashboard
        </h1>
        <p className="text-slate-600 dark:text-slate-300 text-lg">
          Explore {projects?.length || 0} projects and their components
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card card-hover text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {projects?.length || 0}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Total Projects
          </div>
        </div>
        <div className="card card-hover text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {totalComponents}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Total Components
          </div>
        </div>
        <div className="card card-hover text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {Array.from(new Set(projects?.flatMap(p => [...(p.owner || []), ...(p.maintainers || [])]) || [])).length}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Team Members
          </div>
        </div>
      </div>

      {/* Components Table */}
      {projects.length === 0 ? (
        <div className="text-center py-12">
          <Database className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            No projects found
          </h3>
          <p className="text-slate-600 dark:text-slate-300">
            Please check your API connection or data.
          </p>
        </div>
      ) : (
        <UnifiedTableView projects={projects} />
      )}
    </div>
  );
};

export default Dashboard;
